import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import AppLayout from '@/components/AppLayout';
import { useAuth } from '@/context/AuthContext';
import { API } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { toast } from 'sonner';
import {
  MessageSquare,
  Users,
  AlertCircle,
  RefreshCw,
  ChevronRight,
  Sparkles,
  Heart,
  Briefcase,
  Activity,
  Rocket,
  Church,
  TrendingUp,
  GraduationCap,
  Building,
  Newspaper,
  AtSign
} from 'lucide-react';

const iconMap = {
  Heart: Heart,
  Briefcase: Briefcase,
  Activity: Activity,
  Rocket: Rocket,
  Church: Church,
  TrendingUp: TrendingUp,
  GraduationCap: GraduationCap,
  Users: Users,
  Building: Building,
  Newspaper: Newspaper,
  MessageCircle: MessageSquare,
  Upload: MessageSquare,
};

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [detailSheetOpen, setDetailSheetOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailData, setDetailData] = useState({ title: '', items: [] });

  const fetchData = useCallback(async () => {
    try {
      const [statsRes, groupsRes] = await Promise.all([
        fetch(`${API}/stats`, { credentials: 'include' }),
        fetch(`${API}/groups`, { credentials: 'include' })
      ]);

      if (statsRes.ok) {
        const statsData = await statsRes.json();
        setStats(statsData);
      }

      if (groupsRes.ok) {
        const groupsData = await groupsRes.json();
        setGroups(groupsData);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleGenerateSandbox = async () => {
    setGenerating(true);
    try {
      const response = await fetch(`${API}/sandbox/generate`, {
        method: 'POST',
        credentials: 'include'
      });

      if (response.ok) {
        toast.success('Dados do Sandbox gerados com sucesso!');
        fetchData();
      } else {
        toast.error('Erro ao gerar dados do Sandbox');
      }
    } catch (error) {
      toast.error('Erro ao conectar com o servidor');
    } finally {
      setGenerating(false);
    }
  };

  const handleSimulateScan = async () => {
    setRefreshing(true);
    try {
      const response = await fetch(`${API}/sandbox/add-messages`, {
        method: 'POST',
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        toast.success(data.message);
        fetchData();
      }
    } catch (error) {
      toast.error('Erro ao simular varredura');
    } finally {
      setRefreshing(false);
    }
  };

  const getIcon = (iconName) => {
    const IconComponent = iconMap[iconName] || MessageSquare;
    return IconComponent;
  };

  const openDetailSheet = async (detailType) => {
    setDetailLoading(true);
    setDetailSheetOpen(true);
    try {
      const response = await fetch(`${API}/dashboard/details?detail_type=${detailType}`, {
        credentials: 'include'
      });
      if (!response.ok) {
        throw new Error('Falha ao carregar detalhes');
      }
      setDetailData(await response.json());
    } catch (error) {
      toast.error('Erro ao carregar detalhes das conversas');
      setDetailSheetOpen(false);
    } finally {
      setDetailLoading(false);
    }
  };

  if (loading) {
    return (
      <AppLayout>
        <div className="p-8">
          <div className="animate-pulse space-y-6">
            <div className="h-8 w-48 bg-zinc-200 rounded" />
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[1, 2, 3, 4].map(i => (
                <div key={i} className="h-24 bg-zinc-200 rounded-xl" />
              ))}
            </div>
            <div className="space-y-3">
              {[1, 2, 3].map(i => (
                <div key={i} className="h-20 bg-zinc-200 rounded-xl" />
              ))}
            </div>
          </div>
        </div>
      </AppLayout>
    );
  }

  const hasGroups = groups.length > 0;
  const accountSegment = user?.account_segment === 'business' ? 'business' : 'personal';

  return (
    <AppLayout sandboxMode={stats?.sandbox_mode}>
      <div className="p-6 md:p-8 max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="font-heading text-2xl md:text-3xl font-bold text-zinc-900">
              Dashboard
            </h1>
            <p className="text-zinc-500 mt-1">
              {accountSegment === 'business'
                ? 'Visão geral da sua comunicação operacional'
                : 'Visão geral das suas conversas'}
            </p>
          </div>
          
          {hasGroups && (
            <Button
              onClick={handleSimulateScan}
              disabled={refreshing}
              className="rounded-full"
              data-testid="simulate-scan-btn"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
              Simular Nova Varredura
            </Button>
          )}
        </div>

        {/* Empty State */}
        {!hasGroups ? (
          <div className="bg-white rounded-2xl border border-zinc-200 p-12 text-center">
            <div className="w-16 h-16 rounded-2xl bg-violet-100 flex items-center justify-center mx-auto mb-6">
              <Sparkles className="w-8 h-8 text-violet-600" />
            </div>
            <h2 className="font-heading text-xl font-semibold text-zinc-900 mb-2">
              {accountSegment === 'business' ? 'Bem-vindo ao seu painel Business!' : 'Bem-vindo ao Zapt Resumo!'}
            </h2>
            <p className="text-zinc-500 mb-8 max-w-md mx-auto">
              {accountSegment === 'business'
                ? 'Para começar, ative o modo Sandbox com dados simulados da operação ou importe grupos da sua rotina profissional.'
                : 'Para começar, ative o modo Sandbox com dados simulados ou importe suas conversas.'}
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                onClick={handleGenerateSandbox}
                disabled={generating}
                size="lg"
                className="rounded-full"
                data-testid="generate-sandbox-btn"
              >
                {generating ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    Gerando...
                  </>
                ) : (
                  <>
                    <Sparkles className="w-4 h-4 mr-2" />
                    Ativar Modo Sandbox
                  </>
                )}
              </Button>
              
              <Button
                variant="outline"
                size="lg"
                onClick={() => navigate('/app/import')}
                className="rounded-full"
                data-testid="import-btn"
              >
                Importar Conversas
              </Button>
            </div>
          </div>
        ) : (
          <>
            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
              <Card
                className="stat-card border-zinc-200 cursor-pointer hover:border-fuchsia-300 transition-colors"
                onClick={() => openDetailSheet('mentions')}
              >
                <CardContent className="p-5">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center">
                      <Users className="w-5 h-5 text-blue-600" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-zinc-900">{stats?.groups_count || 0}</p>
                      <p className="text-xs text-zinc-500">Grupos Ativos</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card
                className="stat-card border-zinc-200 cursor-pointer hover:border-orange-300 transition-colors"
                onClick={() => openDetailSheet('needs_response')}
              >
                <CardContent className="p-5">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-violet-100 flex items-center justify-center">
                      <MessageSquare className="w-5 h-5 text-violet-600" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-zinc-900">{stats?.total_messages || 0}</p>
                      <p className="text-xs text-zinc-500">Mensagens</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="stat-card border-zinc-200">
                <CardContent className="p-5">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-fuchsia-100 flex items-center justify-center">
                      <AtSign className="w-5 h-5 text-fuchsia-600" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-zinc-900">{stats?.directed_to_user_count || 0}</p>
                      <p className="text-xs text-zinc-500">Falaram com Você</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="stat-card border-zinc-200">
                <CardContent className="p-5">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-orange-100 flex items-center justify-center">
                      <AlertCircle className="w-5 h-5 text-orange-600" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-zinc-900">{stats?.needs_response || 0}</p>
                      <p className="text-xs text-zinc-500">Precisam Resposta</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Groups List */}
            <div>
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-heading text-lg font-semibold text-zinc-900">Grupos</h2>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={() => navigate('/app/daily')}
                  className="text-violet-600"
                >
                  Ver Resumo do Dia
                  <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </div>

              <div className="space-y-3">
                {groups.map((group) => {
                  const IconComponent = getIcon(group.icon);
                  return (
                    <Card 
                      key={group.group_id}
                      className="card-hover cursor-pointer border-zinc-200"
                      onClick={() => navigate(`/app/groups/${group.group_id}`)}
                      data-testid={`group-card-${group.group_id}`}
                    >
                      <CardContent className="p-4 flex items-center gap-3 sm:gap-4">
                        <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-zinc-100 flex items-center justify-center flex-shrink-0">
                          <IconComponent className="w-5 h-5 sm:w-6 sm:h-6 text-zinc-600" />
                        </div>
                        
                        <div className="flex-1 min-w-0 overflow-hidden">
                          <div className="flex items-center gap-2 mb-1">
                            <h3 className="font-heading font-semibold text-zinc-900 truncate flex-1" title={group.name}>
                              {group.name}
                            </h3>
                            <Badge variant="outline" className="text-xs bg-zinc-100 text-zinc-600 border-0 flex-shrink-0 hidden sm:inline-flex">
                              {group.category}
                            </Badge>
                          </div>
                          <div className="flex items-center gap-2 flex-wrap">
                            <p className="text-xs sm:text-sm text-zinc-500">
                              <span className="sm:hidden">{group.message_count} msgs</span>
                              <span className="hidden sm:inline">{group.message_count} mensagens hoje</span>
                            </p>
                            {(group.directed_to_user_count || 0) > 0 && (
                              <Badge className="bg-fuchsia-100 text-fuchsia-700 hover:bg-fuchsia-100 border-0 text-[10px] sm:text-xs">
                                <span className="sm:hidden">{group.directed_to_user_count}</span>
                                <span className="hidden sm:inline">{group.directed_to_user_count} com você</span>
                              </Badge>
                            )}
                            {group.needs_response > 0 && (
                              <Badge className="bg-orange-100 text-orange-700 hover:bg-orange-100 border-0 text-[10px] sm:text-xs">
                                <span className="sm:hidden">{group.needs_response}</span>
                                <span className="hidden sm:inline">{group.needs_response} responder</span>
                              </Badge>
                            )}
                          </div>
                        </div>
                        
                        <ChevronRight className="w-5 h-5 text-zinc-400 flex-shrink-0" />
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </div>

            <Sheet open={detailSheetOpen} onOpenChange={setDetailSheetOpen}>
              <SheetContent side="right" className="w-full sm:max-w-2xl overflow-y-auto">
                <SheetHeader>
                  <SheetTitle>{detailData.title || 'Detalhes das conversas'}</SheetTitle>
                  <SheetDescription>
                    Veja os grupos e as mensagens relacionadas a este indicador.
                  </SheetDescription>
                </SheetHeader>

                <div className="mt-6 space-y-3">
                  {detailLoading ? (
                    <div className="space-y-3">
                      {[1, 2, 3].map((item) => (
                        <div key={item} className="h-24 rounded-xl bg-zinc-100 animate-pulse" />
                      ))}
                    </div>
                  ) : detailData.items?.length > 0 ? (
                    detailData.items.map((item) => (
                      <button
                        key={item.message_id}
                        type="button"
                        onClick={() => navigate(`/app/groups/${item.group_id}`)}
                        className="w-full rounded-xl border border-zinc-200 bg-white p-4 text-left hover:border-violet-300 hover:bg-violet-50/30 transition-colors"
                      >
                        <div className="flex items-center justify-between gap-3 mb-2">
                          <div>
                            <p className="font-medium text-zinc-900">{item.group_name}</p>
                            <p className="text-xs text-zinc-500">
                              {item.sender} • {item.date ? new Date(`${item.date}T12:00:00`).toLocaleDateString('pt-BR') : '-'}
                            </p>
                          </div>
                          <Badge className={`${item.needs_action ? 'bg-orange-100 text-orange-700' : 'bg-emerald-100 text-emerald-700'} border-0`}>
                            {item.action_label}
                          </Badge>
                        </div>
                        <p className="text-sm text-zinc-700 line-clamp-3">{item.content}</p>
                      </button>
                    ))
                  ) : (
                    <div className="rounded-xl border border-dashed border-zinc-300 p-6 text-center text-zinc-500">
                      Nenhuma conversa encontrada neste indicador.
                    </div>
                  )}
                </div>
              </SheetContent>
            </Sheet>
          </>
        )}
      </div>
    </AppLayout>
  );
}
